import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { csv } from '../api/index';
import type { CsvColumnMapping, CsvBatchDefaults, CsvMappingConfig, SavedCsvMapping } from '../api/index';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { CAST_ROLE_OPTIONS } from '../lib/castRoles.js';

// Field list MUST stay in sync with src/parsers/csv/types.ts CANONICAL_FIELDS.
// We intentionally inline (rather than importing) so the frontend bundle does
// not pull in server-side modules.
const CANONICAL_FIELDS: Array<{
  value: string;
  label: string;
  isArray?: boolean;
  isContributor?: boolean;
  isDate?: boolean;
  isBool?: boolean;
}> = [
  { value: 'ignore', label: 'Ignore' },

  { value: 'name', label: 'Title / Name' },
  { value: 'slug', label: 'Slug' },
  { value: 'isbn13', label: 'ISBN-13' },
  { value: 'bookId', label: 'Book ID (internal)' },

  { value: 'seriesName', label: 'Series Name' },
  { value: 'seriesPosition', label: 'Series Position (number)' },
  { value: 'groupName', label: 'Group / Season Name' },

  { value: 'contributor', label: 'Contributor (requires role)', isContributor: true },
  { value: 'publisherName', label: 'Publisher' },
  { value: 'imprintNames', label: 'Imprints', isArray: true },
  { value: 'visibilityNames', label: 'Visibilities', isArray: true },
  { value: 'genreNames', label: 'Genres', isArray: true },
  { value: 'bisac', label: 'BISAC Codes', isArray: true },
  { value: 'tags', label: 'Tags', isArray: true },
  { value: 'keywords', label: 'Keywords', isArray: true },
  { value: 'language', label: 'Language' },
  { value: 'edition', label: 'Edition' },
  { value: 'countryCodes', label: 'Country Codes', isArray: true },

  { value: 'releaseAt', label: 'Release Date', isDate: true },
  { value: 'originalReleaseAt', label: 'Original Release Date', isDate: true },
  { value: 'preorderReleaseAt', label: 'Preorder Release Date', isDate: true },
  { value: 'showReleaseDateOnPreorder', label: 'Show Release Date On Preorder', isBool: true },

  { value: 'description', label: 'Description (long)' },
  { value: 'webSynopsis', label: 'Web Synopsis' },
  { value: 'shortDescription', label: 'Short Description' },
  { value: 'summary', label: 'Summary' },

  { value: 'price', label: 'Price (number)' },
  { value: 'free', label: 'Free?', isBool: true },
  { value: 'purchasable', label: 'Purchasable?', isBool: true },
  { value: 'featuredOnApp', label: 'Featured On App?', isBool: true },
  { value: 'isArchived', label: 'Archived?', isBool: true },
  { value: 'status', label: 'Status (draft / published / archived)' },

  { value: 'format', label: 'Format (mp3 / m4b)' },
  { value: 'formats', label: 'Formats (multi)', isArray: true },
  { value: 'type', label: 'Type (audioFile etc)' },
  { value: 'displayOptions', label: 'Display Options' },

  { value: 'coverImageUrl', label: 'Cover Image URL (informational — not yet uploaded)' },
  { value: 'sampleUrl', label: 'Sample URL' },

  { value: 'totalDuration', label: 'Total Duration (sec or HH:MM:SS)' },
];

const DEFAULT_FIELD_LABELS: Record<string, string> = {
  seriesName: 'Series Name',
  groupName: 'Group / Season Name',
  publisherName: 'Publisher',
  imprint: 'Imprint',
  visibility: 'Visibility',
  genre: 'Genre',
  status: 'Status',
  format: 'Format',
  type: 'Type',
  language: 'Language',
};

export default function CsvMapper() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();

  // Column mappings state
  const [columns, setColumns] = useState<CsvColumnMapping[]>([]);
  // Batch defaults
  const [defaults, setDefaults] = useState<CsvBatchDefaults>({});
  // Save mapping options
  const [saveMapping, setSaveMapping] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [savedPick, setSavedPick] = useState('');

  const canonicalOptions = useMemo(
    () => CANONICAL_FIELDS.map((f) => ({ value: f.value, label: f.label })),
    [],
  );

  // Load preview data
  const { data: preview, isLoading } = useQuery({
    queryKey: ['csv-preview', batchId],
    queryFn: () => csv.preview(batchId!),
    enabled: !!batchId,
  });

  // Load saved mappings
  const { data: savedData } = useQuery({
    queryKey: ['csv-saved-mappings'],
    queryFn: () => csv.savedMappings(),
  });

  const savedMappingOptions = useMemo(() => {
    const list = savedData?.mappings ?? [];
    return [{ value: '', label: '— select —' }, ...list.map((m) => ({ value: m.id, label: m.name }))];
  }, [savedData?.mappings]);

  // Init columns from headers
  useEffect(() => {
    if (preview?.headers && columns.length === 0) {
      setColumns(preview.headers.map(h => ({ csvHeader: h, canonicalField: 'ignore' })));
    }
  }, [preview?.headers]);

  const submitMutation = useMutation({
    mutationFn: () => csv.submitMapping(
      batchId!,
      { columns, defaults } satisfies CsvMappingConfig,
      saveMapping && saveName ? saveName : undefined,
    ),
    onSuccess: (res) => navigate(`/batches/${res.batchId}`),
  });

  function updateColumn(idx: number, patch: Partial<CsvColumnMapping>) {
    setColumns(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  }

  function loadSavedMapping(saved: SavedCsvMapping) {
    const cfg = saved.mappingJson;
    setDefaults(cfg.defaults ?? {});
    // Re-index by header — only apply to columns that still exist
    if (preview?.headers) {
      setColumns(prev => prev.map(col => {
        const match = cfg.columns.find(c => c.csvHeader === col.csvHeader);
        return match ?? { csvHeader: col.csvHeader, canonicalField: 'ignore' };
      }));
    }
  }

  if (!batchId) return <div className="p-8 text-red-600">No batch ID provided.</div>;
  if (isLoading) return <div className="p-8 text-gray-400">Loading CSV preview…</div>;
  if (!preview) return <div className="p-8 text-red-600">Failed to load preview.</div>;

  const fieldMeta = (val: string) => CANONICAL_FIELDS.find(f => f.value === val);

  const INPUT_CLS = 'border border-gray-300 text-gray-900 text-sm rounded px-3 py-1.5 w-full focus:outline-none focus:ring-1 focus:ring-brand-500 placeholder-gray-400';

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Map CSV Columns</h1>
        <p className="text-sm text-gray-500 mt-1">
          Tell us what each column means. Set batch defaults for fields not in the CSV.
        </p>
      </div>

      {/* Saved mappings loader */}
      {savedData?.mappings && savedData.mappings.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-4 flex-wrap">
          <span className="text-sm text-gray-600 shrink-0">Load saved mapping:</span>
          <div className="flex-1 min-w-[12rem] max-w-md">
            <SearchableSelect
              value={savedPick}
              onChange={(id) => {
                setSavedPick(id);
                const m = savedData.mappings.find((x) => x.id === id);
                if (m) loadSavedMapping(m);
              }}
              options={savedMappingOptions}
              placeholder="Saved mapping"
              allowClear={false}
            />
          </div>
        </div>
      )}

      {/* Section A — Batch defaults */}
      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-800">Batch Defaults</h2>
          <p className="text-xs text-gray-500 mt-0.5">Applied to every row. Per-row column values override these.</p>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Object.entries(DEFAULT_FIELD_LABELS).map(([key, label]) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-xs text-gray-600 font-medium">{label}</span>
              <input
                type="text"
                value={(defaults as Record<string, string>)[key] ?? ''}
                onChange={e => setDefaults(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={`e.g. ${key === 'seriesName' ? 'The Ember Chronicles' : key === 'status' ? 'draft' : ''}`}
                className={INPUT_CLS}
              />
            </label>
          ))}
        </div>
      </section>

      {/* Section B — Column mapping */}
      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-800">Column Mapping</h2>
          <p className="text-xs text-gray-500 mt-0.5">Map each CSV column to a canonical field.</p>
        </div>

        {/* Sample rows preview */}
        <div className="overflow-x-auto border-b border-gray-200">
          <table className="text-xs min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {preview.headers.map(h => (
                  <th key={h} className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.sampleRows.map((row, ri) => (
                <tr key={ri} className="border-t border-gray-100">
                  {preview.headers.map(h => (
                    <td key={h} className="px-3 py-1.5 whitespace-nowrap text-gray-600 max-w-[180px] truncate">{row[h]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mapping rows */}
        <div className="divide-y divide-gray-100">
          {columns.map((col, idx) => {
            const meta = fieldMeta(col.canonicalField);
            return (
              <div key={col.csvHeader} className="px-5 py-3 flex flex-wrap items-center gap-3">
                <span className="text-sm text-gray-700 w-44 shrink-0 font-mono">{col.csvHeader}</span>
                <span className="text-gray-400 text-sm">→</span>

                <div className="min-w-[200px] flex-1 max-w-md">
                  <SearchableSelect
                    value={col.canonicalField}
                    onChange={(v) =>
                      updateColumn(idx, { canonicalField: v, role: undefined, delimiter: undefined })
                    }
                    options={canonicalOptions}
                    placeholder="Field"
                    allowClear={false}
                  />
                </div>

                {meta?.isContributor && (
                  <div className="min-w-[10rem] w-44 shrink-0">
                    <SearchableSelect
                      value={col.role ?? 'E07'}
                      onChange={(v) => updateColumn(idx, { role: v })}
                      options={CAST_ROLE_OPTIONS}
                      placeholder="Role"
                      allowClear={false}
                    />
                  </div>
                )}

                {/* Delimiter sub-input for array fields */}
                {(meta?.isArray || meta?.isContributor) && (
                  <label className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Split by:</span>
                    <input
                      type="text"
                      value={col.delimiter ?? ''}
                      onChange={e => updateColumn(idx, { delimiter: e.target.value || undefined })}
                      placeholder="|"
                      className="border border-gray-300 text-gray-800 text-sm rounded px-2 py-1 w-10 text-center focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <label className="flex items-center gap-3 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={saveMapping}
            onChange={e => setSaveMapping(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          Save this mapping for reuse
          {saveMapping && (
            <input
              type="text"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              placeholder="e.g. Macmillan Standard"
              className="border border-gray-300 text-gray-900 text-sm rounded px-3 py-1.5 ml-2 w-52 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          )}
        </label>

        <button
          onClick={() => submitMutation.mutate()}
          disabled={submitMutation.isPending}
          className="bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium px-6 py-2 rounded-lg transition-colors"
        >
          {submitMutation.isPending ? 'Starting import…' : 'Start Import'}
        </button>
      </div>

      {submitMutation.isError && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded px-4 py-3">
          {submitMutation.error instanceof Error ? submitMutation.error.message : String(submitMutation.error)}
        </div>
      )}
    </div>
  );
}
